"use client"

import BulletList from "@/ui/common/bit/BulletList"
import LottiePlayer from "@/ui/common/bit/LottiePlayer"
import { isLottieFile } from "@/ui/common/bit/isLottieFile"
import { colors } from "@/ui/theme/colors"
import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import React from "react"
import { mediaQueries } from "../../theme/mediaQueries"
import { ApplicationImageAndContentBlockProps } from "./types"

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  width: 100%;

  ${mediaQueries.desktopM} {
    flex-direction: row;
  }
`
const ContentContainer = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${mediaQueries.desktopM} {
    width: 50%;
  }
`

const Subtitle = styled(Typography)<{ component?: string }>`
  color: white;
`

const Title = styled(Typography)<{ component?: string }>`
  color: white;
`

const AppTitle = styled(Typography)<{ component?: string }>`
  color: white;
  margin-top: 16px;
`

const AppLinksContainer = styled(Box)`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`

const StyledAppLink = styled("a")`
  display: block;
  position: relative;
  height: 40px;
  width: 135px;
`
const StyledImageContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 270px;
  img {
    object-fit: contain;
  }
  ${mediaQueries.desktopM} {
    width: 650px;
    height: 550px;
  }
`

const StyledImage = styled(Image)`
  object-fit: cover;
`

const ApplicationImageAndContentBlock: React.FC<
  ApplicationImageAndContentBlockProps
> = (props) => {
  const {
    subtitle,
    title,
    text,
    appTitle,
    appStoreLogo,
    appStoreLink,
    googlePlayLogo,
    googlePlayLink,
    image,
    className,
  } = props

  return (
    <Container className={className}>
      <ContentContainer>
        {subtitle && (
          <Subtitle variant="subtitle1" component="p">
            {subtitle}
          </Subtitle>
        )}
        {title && (
          <Title variant="h4" component="h2">
            {title}
          </Title>
        )}
        {text && (
          <BulletList
            dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(text) }}
            iconRoute="/icons/checks/check_blue_white_line.svg"
            textColor={colors.white}
          />
        )}
        {appTitle && (
          <AppTitle variant="h6" component="p">
            {appTitle}
          </AppTitle>
        )}

        <AppLinksContainer>
          {appStoreLogo && appStoreLink && (
            <StyledAppLink
              href={appStoreLink?.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              <StyledImage
                src={appStoreLogo?.src}
                alt={appStoreLogo?.alt}
                fill
              />
            </StyledAppLink>
          )}

          {googlePlayLogo && googlePlayLink?.href && googlePlayLink && (
            <StyledAppLink
              href={googlePlayLink?.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              <StyledImage
                src={googlePlayLogo?.src}
                alt={googlePlayLogo?.alt}
                fill
              />
            </StyledAppLink>
          )}
        </AppLinksContainer>
      </ContentContainer>
      {image && image.src && (
        <StyledImageContainer>
          {isLottieFile(image.src) ? (
            <LottiePlayer
              lottieUrl={image.src}
              playOnce={true}
              autoplay={true}
              loop={false}
              ariaLabel={image.alt}
              style={{ width: "100%", height: "100%" }}
            />
          ) : (
            <Image src={image?.src} alt={image?.alt} fill />
          )}
        </StyledImageContainer>
      )}
    </Container>
  )
}

export default ApplicationImageAndContentBlock
